/**
 * The project's audit log (`MCK-20260521-009`, C7).
 *
 * **The event vocabulary is the copy's real work.** A log that printed `hierarchy_node_created`
 * is a log only its authors can read. Each event type gets a sentence in the language of the
 * act — *"added a level"*, *"published a version"* — and an unknown type falls back to the raw
 * name rather than to nothing, because a row nobody can name is still a row that happened.
 *
 * `scoped.notice` is the one non-negotiable string on this screen. A filtered audit trail that
 * does not say it is filtered reads as an absence of events, which is the single failure this
 * record cannot have (`MCK-20260521-005` invariant 6).
 */

export const activityEn = {
  'page.title': 'Activity',
  'page.subtitle': 'What has happened on this project, newest first.',

  'status.loading': 'Loading the log…',
  'status.explains': 'Activity is the record of what happened on the project, and who did it.',
  'status.emptyTitle': 'Nothing recorded yet',
  'status.empty': 'Nothing has been recorded yet.',
  // A separate sentence from `status.empty`, because they are separate facts. A filtered log
  // that says *nothing has been recorded yet* is the one wrong answer an audit trail cannot
  // give (`MCK-20260521-005` invariant 6, applied to the filter rather than to the zone).
  'status.noMatchTitle': 'No matching events',
  'status.noMatch': 'No events match this filter.',
  'list.more': 'Show older',
  'list.loadingMore': 'Loading…',

  // C9's filter bar. The three sets are any-of, so each picker rests on the question it is
  // asking rather than on an empty box: *Anybody*, *Any event*, *Anything*.
  'filter.legend': 'Narrow the log',
  // The collapsed bar's trigger and its ✕ (`KLQ-20260910-001` §B). The number beside the
  // trigger counts narrowed *fields*, not values.
  'filter.open': 'Filters',
  'filter.close': 'Close filters',
  'filter.actor': 'Who',
  'filter.actorAny': 'Anybody',
  'filter.event': 'What',
  'filter.eventAny': 'Any event',
  'filter.entity': 'About',
  'filter.entityAny': 'Anything',
  'filter.since': 'From',
  'filter.until': 'Until',
  'filter.remove': 'Remove {name}',
  'filter.clear': 'Clear filters',
  // The search term's chip in the collapsed bar.
  'filter.search': 'Search: {term}',

  // The search box (`KLQ-20260821-003`). It matches what the events *carry* — codes, level
  // names, version numbers — and never a person, so the placeholder names the nouns it finds.
  // Who did something is the *Who* filter's question.
  'search.label': 'Search the log',
  'search.placeholder': 'Codes, levels, versions…',

  // The file carries every column the rows above show, plus each event's proof string.
  'export.action': 'Export CSV',

  'scoped.notice':
    'You are seeing the parts of this log that cover your zones. Events elsewhere in the project are not shown.',

  'actor.unknown': 'Someone',
  'actor.system': 'Kalqa',
  'role.at': 'as {role}',

  // The verbs. Written as *"[somebody] did this"*, so a row reads as a sentence with the actor
  // in front of it.
  //
  // TWIN: the key after `event.` is the backend's `event_type`, verbatim, and the set of types
  // we render is `features/activity/events.ts`'s `KNOWN`. A key spelled differently from its
  // emitter is not a broken build — it is a row that silently falls through to
  // `event.unknown` while this sentence sits unread. Four of them did exactly that until
  // 2026-08-21 (`pin_added` for `pin_created`, `member_*` for `project_member_*`).
  'event.project_created': 'created the project',
  'event.project_updated': 'changed the project’s details',
  'event.project_archived': 'archived the project',
  'event.hierarchy_seeded': 'started the structure from a preset',
  'event.hierarchy_generated': 'generated {count} levels',
  'event.hierarchy_duplicated': 'duplicated a level',
  'event.hierarchy_grouped': 'put levels inside a new one',
  'event.hierarchy_node_created': 'added a level',
  'event.hierarchy_node_updated': 'renamed or re-coded a level',
  'event.hierarchy_node_removed': 'removed a level',
  'event.hierarchy_reordered': 'moved levels around',
  'event.drawing_slot_created': 'added a drawing',
  'event.version_published': 'published a version',
  'event.version_rolled_back': 'rolled a drawing back',
  'event.comment_added': 'commented on a drawing',
  'event.comment_resolved': 'resolved a comment',
  'event.comment_audience_changed': 'made a comment visible to the project',
  'event.pin_created': 'pinned a spot on a drawing',
  'event.pin_resolved': 'resolved a pin',
  'event.review_requested': 'asked for a review',
  'event.review_decided': 'decided a review',
  'event.review_cancelled': 'withdrew a review',
  'event.project_member_added': 'added somebody to the project',
  'event.project_member_removed': 'removed somebody from the project',
  'event.project_member_role_changed': 'changed somebody’s role',
  'event.unknown': '{type}',

  'entity.hierarchy_node': 'level',
  'entity.drawing_slot': 'drawing',
  'entity.version': 'version',
  'entity.review': 'review',
  'entity.comment': 'comment',
  'entity.pin': 'pin',
  'entity.project': 'project',
  'entity.project_membership': 'membership',

  'error.forbidden': 'You are not allowed to read this project’s log.',
  'error.gone': 'This project is no longer here.',
  'error.rateLimited': 'Too many requests. Wait a moment and try again.',
  'error.cursor': 'That list moved on. Reload it to see the latest.',
  'error.unexpected': 'Something went wrong. Try again.',
} as const;

export type ActivityMessageKey = keyof typeof activityEn;
